import type { CombatSkill } from "./petModels.js";
import type { CombatAction } from "../types/pvpTypes.js";
import { COMBAT_SPECIAL_EFFECT } from "./petModels.js";
import { COMBAT_CONSTANTS, calculateBleedPerTurn } from "../constants/battleConstants.js";

/**
 * **THE FOUR GESTURES, ONE ICON EACH — IDENTICAL FOR EVERY SPECIES.**
 *
 * A battle asks the same four questions of every Suikamon, and the buttons that ask them
 * used to change their faces with the pet you were looking at: Pikoto's attack was 🪶,
 * Aqua's was 🔱, Kobu's was 🌩️, and magic followed the species' ELEMENT on top of that. So
 * the icon never meant "attack" — it meant "this pet's attack", and a player had to relearn
 * the row for their own pet, for every opponent, and again after every evolution. Dodge and
 * recover were already shared, which is exactly why those two were the easy ones to read.
 *
 * These four are now the whole vocabulary, and they are deliberately the CARD SUIT ICONS:
 * `EFFECT_SUIT_ROLE` imports this map rather than declaring its own, so the rail on a card
 * and the button that fires it are the same glyph and the same word, always.
 *
 * A NAMED MOVE keeps its own icon. `CombatSkill.emoji` is Feather Dart's 🪶, not attack's —
 * it belongs beside the move's NAME (the skill tooltip's header, the species' combat
 * preview) and never on the gesture button, which is what this map is for.
 *
 * **THESE FOUR MUST NOT COLLIDE WITH ANYTHING ELSE THE ARENA DRAWS**, which is what rules
 * out the two obvious alternatives:
 *
 *   - Dodge is 💨, NOT 🛡️. The arena already prints 🛡️ on the SHIELD status badge and in
 *     Shika's shield lines, and shield is a condition a dodge can leave behind — so the
 *     gesture and one of its outcomes would have worn the same face.
 *   - Recover is ❤️, NOT 🌿. 🌿 is the NATURE element in `SKILL_TYPE_EMOJI`, worn by every
 *     Nature species. ❤️ is already the heart in the recovers-left indicator beside the HP
 *     bar and the icon on every heal stat row, so the gesture, the counter of how many you
 *     have left, and the thing it does are finally one symbol instead of three.
 */
export const COMBAT_ACTION_EMOJI: Record<CombatAction, string> = {
  attack: "⚔️",
  magic: "✨",
  dodge: "💨",
  recover: "❤️",
};

/**
 * **THE FOUR GESTURES, PAINTED.** The sixteen cards each ship an illustration and the four
 * gestures that fire them shipped as system emoji, so the one row a player touches every
 * round was the only thing in the arena drawn by their OS font — a different face on iOS,
 * Android and Windows, and none of them the game's.
 *
 * These are the same four subjects the emoji named — crossed swords, a sparkle, a gust, a
 * heart — so nothing a player already learned is invalidated; only the rendering changes.
 * Palette is `GESTURE_ART_PALETTE` (`shared/domain/artStyle.ts`) for three of the four.
 *
 * **RECOVER'S HEART IS RED (`#F43F5E`), NOT the gesture's green `#22C55E` — deliberately.**
 * The gesture palette exists so a CARD can never clash with whichever Suikamon is holding it;
 * these four are not cards, they are the fixed row of controls, and this one has to match the
 * two things the arena already draws in red beside it: the ❤️ in {@link COMBAT_ACTION_EMOJI}
 * and the recovers-left hearts by the HP bar, whose glow is `rgba(239, 68, 68, …)`. A green
 * heart made the button, the counter of how many you have left, and the thing it does three
 * different colours again — the exact split the emoji map was written to close. Rose rather
 * than attack's scarlet `#EF4444` so the two never read as one suit at 96px. The recover CARD
 * arts stay green; `GESTURE_ART_PALETTE` is unchanged and still governs them.
 *
 * {@link COMBAT_ACTION_EMOJI} is NOT retired — it is the fallback wherever an icon must be
 * plain text (the battle log, Shika's commentary, a tooltip's inline run) and the text a
 * screen reader gets behind the image.
 */
export const COMBAT_ACTION_ART: Record<CombatAction, string> = {
  attack: "/cards/gesture-attack.png",
  magic: "/cards/gesture-magic.png",
  dodge: "/cards/gesture-dodge.png",
  recover: "/cards/gesture-recover.png",
};

/** The painted face for a gesture — same for every species, every element, every level. */
export function getCombatActionArt(action: CombatAction): string {
  return COMBAT_ACTION_ART[action];
}

export const ATTACK_SKILL_EMOJI = COMBAT_ACTION_EMOJI.attack;
export const MAGIC_SKILL_EMOJI = COMBAT_ACTION_EMOJI.magic;
export const RECOVER_SKILL_EMOJI = COMBAT_ACTION_EMOJI.recover;
export const DODGE_SKILL_EMOJI = COMBAT_ACTION_EMOJI.dodge;
/**
 * **THE STUN.** A fighter that lost the turn without ever having one.
 *
 * Named here because it is not a gesture and never appears in {@link COMBAT_ACTION_EMOJI}, yet
 * it is drawn wherever a gesture would be — the reveal overlay, the tray's Stunned! button, the
 * turn-resolution column, the battle log, Shika's lines. It has always been the same glyph in
 * all of them by coincidence rather than by rule; this is the rule.
 */
export const STUN_EMOJI = "🌀";
/**
 * A CRITICAL HIT — the turn landed for {@link CRITICAL_HIT_MULTIPLIER_LABEL}.
 *
 * One glyph, for the same reason the stun has one: it is written by the engine into the battle
 * log AND drawn by the round-result panel on the side that crit, and those two must be the same
 * mark or the panel is explaining a different event from the one the log reported.
 */
export const CRITICAL_HIT_EMOJI = "💥";
/**
 * A CRITICAL HIT, IN A WORD — the callout slammed over the portrait that landed one.
 *
 * The arena already throws a comic-book word over a fighter every turn ("BANG!", "OOF!",
 * "CLASH!"), rolled at random out of a pool per outcome. `CRITICAL!` used to sit IN that pool,
 * which made it the one word there that named a real mechanic and then fired without it: a
 * player who read it had no way to know whether their move had actually crit, and the turns
 * that did crit said "WHACK!" like any other. So the word is named here instead, beside the
 * glyph, and the callout is *chosen* rather than rolled when a fighter crits.
 *
 * Same reason the emoji is shared: the battle log writes it, the round-result panel draws it,
 * and now the portrait shouts it — three surfaces reporting one event, in one wording.
 */
export const CRITICAL_HIT_WORD = "CRITICAL!";
/**
 * **WHAT A CRIT IS WORTH, AS A FIGURE — the one place the game says it in words.**
 *
 * Derived from {@link COMBAT_CONSTANTS.CRITICAL_HIT_MULTIPLIER}, because it used to be typed.
 * The multiplier was 2 and eleven surfaces said "double": the engine's own effect line, the
 * round-result panel's screen-reader label, the arena callout's, the Help page, the Battle
 * Cards page, the changelog. Moving the number to 1.5 with the word "double" left in place
 * would have been the worst kind of balance change — the one the game keeps describing wrong.
 *
 * Prints without a trailing `.0`, so a whole multiplier reads `×2` and a half one `×1.5`.
 */
export const CRITICAL_HIT_MULTIPLIER_LABEL = `×${COMBAT_CONSTANTS.CRITICAL_HIT_MULTIPLIER}`;
/**
 * The same figure as a PERCENTAGE — `150%` — for prose that reads better that way
 * ("lands for 150%" over "lands for ×1.5"). Same single source, same reason.
 */
export const CRITICAL_HIT_PERCENT_LABEL = `${Math.round(
  COMBAT_CONSTANTS.CRITICAL_HIT_MULTIPLIER * 100
)}%`;
/** Squad battles: a Suikamon entered the field (battle log + switch-in hit cue). */
export const SWAP_IN_EMOJI = "🔄";
/** Squad battles: the entering Suikamon was hit on its way in. */
export const SWAP_IN_HIT_EMOJI = "💥";
/** Squad battles: a potion was used this turn (battle log). */
export const POTION_EMOJI = "💊";
export const DODGE_SKILL_DESCRIPTION = "Attempts to evade an incoming Attack.";
export const COMBAT_ACTION_DISPLAY_ORDER: CombatAction[] = ["attack", "magic", "dodge", "recover"];

export type SkillValueOverrides = {
  damage?: number;
  heal?: number;
  specialValue?: number;
};

/**
 * **THE KINDS OF THING A MOVE PAYS — and every one of them has to be on the sheet.**
 *
 * The list was `damage | heal | drain | stun`, and the three it was missing were exactly the
 * three that make a species' printed damage look small. At level 20 the Magic figures span
 * **12 to 48 — 4x** — and a player comparing Kobu's `12` against Suimon's `48` concluded, quite
 * reasonably, that their Suikamon was four times worse.
 *
 * It is not. `levelScaling.ts` deliberately grows a species' magic damage *or* its special,
 * never both (`hasScalableSpecialEffect`), so the low-damage archetypes carry their payload in
 * `specialValue`: Kobu's `12` also lands a bleed worth **9 a turn for four turns**. Count what
 * the special carries and the spread closes from **4.0x to 2.0x** — and the rest of it is not a
 * damage gap at all, because Stun's 29 buys a skipped turn and Wound's 48 buys four turns of
 * halved healing.
 *
 * Stun already had a chip. Bleed, shield and execute did not, so the sheet was quietly telling
 * three of the five archetypes that they hit for a third of what they do. They are here now.
 *
 * **The chip is not the same claim as the damage number, and must never be folded into it.**
 * A bleed is four turns away, a shield is armour rather than a blow, and an execute is
 * conditional — `PvpSkillButton` folds a value into the damage figure only when the unit
 * matches, and none of these three do. They sit BESIDE the number, which is what a chip is for.
 */
export type CombatSkillPresentationStatKind =
  | "damage"
  | "heal"
  | "drain"
  | "stun"
  | "bleed"
  | "shield"
  | "execute"
  | "wound";

export interface CombatSkillPresentationStat {
  kind: CombatSkillPresentationStatKind;
  icon: string;
  value: number | string;
}

function formatPercent(chance?: number): string {
  return `${Math.round((chance ?? 1) * 100)}%`;
}

function getSkillValues(skill: CombatSkill, overrides?: SkillValueOverrides) {
  return {
    damage: overrides?.damage ?? skill.damage,
    heal: overrides?.heal ?? skill.heal,
    specialValue: overrides?.specialValue ?? skill.specialValue ?? 0,
  };
}

function appendChance(skill: CombatSkill, effectText: string): string {
  const chance = skill.specialChance ?? 1;
  return chance >= 1 ? effectText : `there is a ${formatPercent(chance)} chance to ${effectText}`;
}

function describeDodgeSpecial(skill: CombatSkill, specialValue: number): string | null {
  switch (skill.special) {
    case COMBAT_SPECIAL_EFFECT.Dot:
      return `On a successful Dodge, ${appendChance(skill, `inflict a bleed dealing ${calculateBleedPerTurn(specialValue)} damage on each of the next ${COMBAT_CONSTANTS.BLEED_DURATION_TURNS} turns`)}.`;
    case COMBAT_SPECIAL_EFFECT.Wound:
      return `On a successful Dodge, ${appendChance(skill, `wound the opponent, halving their healing for ${COMBAT_CONSTANTS.WOUND_DURATION_TURNS} turns`)}.`;
    case COMBAT_SPECIAL_EFFECT.Shield:
      return `On a successful Dodge, ${appendChance(skill, `set your shield to ${specialValue} HP`)}.`;
    case COMBAT_SPECIAL_EFFECT.DrainLife:
      return `On a successful Dodge, ${appendChance(skill, `heal ${specialValue} HP at the start of next turn`)}.`;
    case COMBAT_SPECIAL_EFFECT.Stun:
      return `On a successful Dodge, ${appendChance(skill, "stun the opponent, making them lose next turn")}.`;
    default:
      return null;
  }
}

export function describeDodgeSkill(skill: CombatSkill, overrides?: SkillValueOverrides): string {
  const { specialValue } = getSkillValues(skill, overrides);
  const specialDescription = describeDodgeSpecial(skill, specialValue);
  return specialDescription
    ? `${DODGE_SKILL_DESCRIPTION} ${specialDescription}`
    : DODGE_SKILL_DESCRIPTION;
}

/**
 * The icon for a GESTURE — the same one for every species, every element, every level.
 *
 * It used to take the species' own skill emoji and its element and prefer those, which is
 * why the four buttons looked different for every pet. Both parameters are gone rather than
 * ignored: a caller that can still PASS an element would still expect it to matter.
 */
export function getCombatActionEmoji(action: CombatAction): string {
  return COMBAT_ACTION_EMOJI[action];
}

/**
 * The sentence a PUNISH clause adds to a skill's description.
 *
 * Every verb reads a condition the board already carries — a Battle Card, a previous turn,
 * or the caster's own shield — which is why this is one sentence appended to what the skill
 * applies, never a replacement for it. Kept here so the battle UI, the Help page and the
 * Battle Cards archetype tables cannot describe a signature three different ways.
 */
export function describePunish(punish: CombatSkill["punish"]): string | null {
  if (!punish) return null;

  switch (punish.effect) {
    case COMBAT_SPECIAL_EFFECT.ExploitDot:
      return `Detonates an existing bleed for its remaining ticks (up to ${COMBAT_CONSTANTS.DETONATE_MAX_TICKS}), plus ${punish.value} bonus damage.`;
    case COMBAT_SPECIAL_EFFECT.ExploitStun:
      return `+${punish.value} bonus while the opponent is stunned, forced, or blocked.`;
    case COMBAT_SPECIAL_EFFECT.ExploitWound:
      return `+${punish.value} bonus against an already wounded opponent, and heals you that much.`;
    case COMBAT_SPECIAL_EFFECT.ExploitShield:
      return `Spends any shield you were already holding as bonus damage first (up to ${punish.value}).`;
    case COMBAT_SPECIAL_EFFECT.PunishAttack:
      return `+${punish.value} bonus if the opponent used Attack last turn.`;
    default:
      return null;
  }
}

/**
 * A skill's text, with its punish sentence appended when it has one.
 *
 * The split exists so the punish clause is added in exactly ONE place: what a skill applies
 * has a dozen branches below, and appending per-branch is how the two halves of a signature
 * drift apart.
 */
export function describeCombatSkill(
  action: CombatAction,
  skill: CombatSkill,
  overrides?: SkillValueOverrides
): string {
  const applied = describeAppliedEffect(action, skill, overrides);
  const punishText = describePunish(skill.punish);
  return punishText ? `${applied} ${punishText}` : applied;
}

/** What the skill APPLIES — damage, heal and its own `special`. Never the punish clause. */
function describeAppliedEffect(
  action: CombatAction,
  skill: CombatSkill,
  overrides?: SkillValueOverrides
): string {
  const { damage, heal, specialValue } = getSkillValues(skill, overrides);

  if (action === "dodge") {
    return describeDodgeSkill(skill, overrides);
  }

  if (action === "recover") {
    return `Heals ${heal} HP.`;
  }

  if (action === "attack") {
    switch (skill.special) {
      case COMBAT_SPECIAL_EFFECT.ExploitDot:
        return `Deals ${damage} damage. +${specialValue} bonus if opponent has active bleed or burn.`;
      case COMBAT_SPECIAL_EFFECT.ExploitWound:
        return `Deals ${damage} damage. +${specialValue} bonus if opponent is wounded.`;
      case COMBAT_SPECIAL_EFFECT.ExploitStun:
        return `Deals ${damage} damage. +${specialValue} bonus if opponent is stunned.`;
      case COMBAT_SPECIAL_EFFECT.PunishAttack:
        return `Deals ${damage} damage. +${specialValue} bonus if opponent used Attack last turn.`;
      case COMBAT_SPECIAL_EFFECT.Heal:
        return `Deals ${damage} damage and heals ${specialValue} HP.`;
      case COMBAT_SPECIAL_EFFECT.DrainLife:
        return `Deals ${damage} damage and heals ${specialValue} HP next turn.`;
      case COMBAT_SPECIAL_EFFECT.Shield:
        return `Deals ${damage} damage and sets shield to ${specialValue} HP.`;
      case COMBAT_SPECIAL_EFFECT.Wound:
        return `Deals ${damage} damage with ${formatPercent(skill.specialChance)} chance to wound opponent, halving their healing for ${COMBAT_CONSTANTS.WOUND_DURATION_TURNS} turns.`;
      case COMBAT_SPECIAL_EFFECT.Execute:
        return `Deals ${damage} damage. +${specialValue} bonus if opponent is below 50% HP.`;
      default:
        return `Deals ${damage} damage.`;
    }
  }

  switch (skill.special) {
    case COMBAT_SPECIAL_EFFECT.Dot:
      return `Deals ${damage} damage and inflicts a bleed: ${calculateBleedPerTurn(specialValue)} damage on each of the next ${COMBAT_CONSTANTS.BLEED_DURATION_TURNS} turns.`;
    case COMBAT_SPECIAL_EFFECT.Stun: {
      const stunChance = skill.specialChance ?? 1;
      if (stunChance >= 1) {
        return `Deals ${damage} damage and stuns opponent, making them lose next turn. Resisted if target is already stun-immune.`;
      }
      return `Deals ${damage} damage with ${formatPercent(stunChance)} chance to stun opponent, making them lose next turn. Resisted if target is already stun-immune.`;
    }
    case COMBAT_SPECIAL_EFFECT.Wound:
      return `Deals ${damage} damage and wounds opponent, halving their healing for ${COMBAT_CONSTANTS.WOUND_DURATION_TURNS} turns.`;
    // Shield was missing here while the `attack` branch above handled it, so every
    // Shield species' MAGIC — the archetype's whole point — described only its damage
    // and silently dropped the shield. Pinky's kit also heals, so both are reported.
    case COMBAT_SPECIAL_EFFECT.Shield:
      return heal > 0
        ? `Deals ${damage} damage, heals ${heal} HP, and sets shield to ${specialValue} HP.`
        : `Deals ${damage} damage and sets shield to ${specialValue} HP.`;
    case COMBAT_SPECIAL_EFFECT.DrainLife:
      return `Deals ${damage} damage and heals ${specialValue} HP next turn.`;
    case COMBAT_SPECIAL_EFFECT.Execute:
      return `Deals ${damage} damage, or ${damage + specialValue} if opponent is below 50% HP.`;
    default:
      if (heal > 0) {
        return `Deals ${damage} damage and heals ${heal} HP.`;
      }
      return `Deals ${damage} damage.`;
  }
}

/**
 * The stat rows under a skill's description — damage, heal, drain, stun.
 *
 * The ELEMENT parameter is gone with the element-coloured magic icon it existed to pick:
 * every row is now labelled by its GESTURE, which is the same glyph for every species.
 */
export function getCombatSkillPresentationStats(
  action: CombatAction,
  skill: CombatSkill,
  overrides?: SkillValueOverrides
): CombatSkillPresentationStat[] {
  const { damage, heal, specialValue } = getSkillValues(skill, overrides);
  const stats: CombatSkillPresentationStat[] = [];

  if (damage > 0) {
    stats.push({
      // The damage row is labelled by the GESTURE that deals it, not by the move's own
      // icon: it sits in a list beside a heal row and a stun row, and those are gestures.
      kind: "damage",
      icon: getCombatActionEmoji(action),
      value: damage,
    });
  }

  if (skill.special === COMBAT_SPECIAL_EFFECT.Stun) {
    stats.push({
      kind: "stun",
      icon: "🌀",
      value: "Stun",
    });
  }

  /*
   * **THE BLEED — the biggest number a DoT species has, and it was on no sheet at all.**
   *
   * Reported as the RATE and the count (`9/turn x4`), never as one total, for the same reason
   * the status badge is: a rate and a number of turns are different kinds of number, and a
   * lone `36` beside a `12` would read as damage this turn, which it is not. Derived through
   * `calculateBleedPerTurn` — the function the engine ticks with — so the chip cannot advertise
   * a drip the fight does not deal.
   */
  if (skill.special === COMBAT_SPECIAL_EFFECT.Dot && specialValue > 0) {
    stats.push({
      kind: "bleed",
      icon: "🩸",
      value: `${calculateBleedPerTurn(specialValue)}/turn ×${COMBAT_CONSTANTS.BLEED_DURATION_TURNS}`,
    });
  }

  /*
   * **THE WOUND — a word-chip, exactly like Stun's, and for the same reason.**
   *
   * It has no magnitude to print: it halves the target's healing for
   * `WOUND_DURATION_TURNS` turns. Stun set the precedent — an effect that IS the move gets a
   * chip that names it — and the rule has to hold for every archetype or it is not a rule.
   * Wound happens to be the archetype with the biggest printed damage, so this chip helps it
   * least; carving it out on that basis would be balancing the sheet rather than describing
   * the move, which is how a presentation layer starts lying.
   */
  if (skill.special === COMBAT_SPECIAL_EFFECT.Wound) {
    stats.push({
      kind: "wound",
      icon: "🩹",
      value: "Wound",
    });
  }

  /*
   * **THE SHIELD — armour, and labelled as armour.** A Shield species' magic sets a pool that
   * absorbs incoming damage; printing it as a bare number beside the damage figure would claim
   * it as a blow. The icon carries the distinction the way the status badge does.
   */
  if (skill.special === COMBAT_SPECIAL_EFFECT.Shield && specialValue > 0) {
    stats.push({
      kind: "shield",
      icon: "🛡️",
      value: specialValue,
    });
  }

  /*
   * **THE EXECUTE — the whole point of the archetype, and it was prose only.**
   *
   * Printed as what the move ACTUALLY LANDS when the condition holds (`damage + specialValue`),
   * not as the bonus alone: `+35` beside a `20` invites the reader to add them, and a player who
   * adds them is doing the arithmetic the chip exists to save. The threshold rides in the icon's
   * company rather than the value — the tooltip prose beside it states it in full.
   */
  if (skill.special === COMBAT_SPECIAL_EFFECT.Execute && specialValue > 0 && damage > 0) {
    stats.push({
      kind: "execute",
      icon: "☠️",
      value: damage + specialValue,
    });
  }

  if (skill.special === COMBAT_SPECIAL_EFFECT.DrainLife && specialValue > 0) {
    stats.push({
      kind: "drain",
      icon: "💜",
      value: specialValue,
    });
  }

  if (action === "recover" && heal > 0) {
    stats.push({
      kind: "heal",
      icon: "❤️",
      value: heal,
    });
  }

  if (skill.special === COMBAT_SPECIAL_EFFECT.Heal && specialValue > 0) {
    stats.push({
      kind: "heal",
      icon: "❤️",
      value: specialValue,
    });
  }

  if (heal > 0 && action !== "recover" && skill.special !== COMBAT_SPECIAL_EFFECT.DrainLife) {
    stats.push({
      kind: "heal",
      icon: "❤️",
      value: heal,
    });
  }

  return stats;
}

type BaseCombatSkill = Omit<CombatSkill, "description" | "emoji" | "special"> & {
  description?: string;
  emoji?: string;
  special?: CombatSkill["special"];
};

export function defineCombatSkill(
  action: CombatAction,
  skill: BaseCombatSkill
): CombatSkill {
  const combatSkill = {
    ...skill,
    special: skill.special ?? COMBAT_SPECIAL_EFFECT.None,
    // A move may carry its own icon — that is the icon of the MOVE, shown beside its name.
    // Without one it falls back to its gesture's, which is what the shared dodge and
    // recover templates have always done.
    emoji: skill.emoji ?? getCombatActionEmoji(action),
  } as CombatSkill;

  return {
    ...combatSkill,
    description: skill.description ?? describeCombatSkill(action, combatSkill),
  };
}
